import { moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { LayoutHelper } from '../../../helpers/layout.helper';
import { MetaHelper } from '../../../helpers/meta.helper';
import { LayoutElement } from '../../../models/layoutelement.model';
import { LayoutService } from '../../../services/layout.service';
import { BaseThemeControl } from '../base.theme.control';

@Component({
    selector: 'listbox-theme-control',
    templateUrl: './listbox.theme.control.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ListBoxThemeControl extends BaseThemeControl {
    ClearButtonPlaceholder = false;
    ClearButtonInputType = false;
    ClearButtonInputMode = false;

    //#region SelectedEntry
    SelectedEntryValue;
    @Input()
    get SelectedEntry() {
        return this.SelectedEntryValue;
    }
    set SelectedEntry(val) {
        this.SelectedEntryValue = val;
        this.CheckVisibility();
        this.SelectedEntryChange.emit(this.SelectedEntryValue);
    }

    @Output() SelectedEntryChange = new EventEmitter<any>();
    //#endregion
    DisplayMemberPathValue = "Name";
    @Input()
    get DisplayMemberPath() {
        return this.DisplayMemberPathValue;
    }
    set DisplayMemberPath(val) {
        this.DisplayMemberPathValue = val;
        this.cdRef.detectChanges();
    }
    //#region Properties
    PropertiesValue;
    RawProperties;
    @Input()
    get Properties() {
        return this.PropertiesValue;
    }
    set Properties(val) {
        if (val) {
            const rawProperties = [];
            val.forEach(v => {
                rawProperties.push({
                    ...v
                });
            });
            this.RawProperties = rawProperties;
        } else {
            this.RawProperties = null;
        }
        this.CheckVisibility();
        this.PropertiesChange.emit(this.PropertiesValue);
    }

    @Output() PropertiesChange = new EventEmitter<any>();
    //#endregion

    //#region UseCountAsCaption
    UseCountAsCaptionValue = false;
    @Input()
    get UseCountAsCaption() {
        return this.UseCountAsCaptionValue;
    }
    set UseCountAsCaption(val) {
        this.UseCountAsCaptionValue = val;
        this.UseCountAsCaptionChange.emit(this.UseCountAsCaptionValue);
    }

    @Output() UseCountAsCaptionChange = new EventEmitter<any>();
    //#endregion
    inputTypes = [];
    inputModes = [];

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.Subscriptions['SelectedItem'] = LayoutService.SelectedItem.subscribe(() => {
            this.SelectedEntry = null;
            this.cdRef.detectChanges();
        });
    }

    AddItem() {
        const item = this.GenerateNewItem();
        this.SelectedEntry = item;
        if (!this.DataSourceInternal) {
            this.DataSourceInternal = [];
        }
        this.DataSourceInternal.push(item);
        this.onStyleChanged();
        if (item instanceof LayoutElement) {
            LayoutService.OnLayoutElementAdded({
                ElementContent: JSON.stringify(item),
                Index: this.DataSourceInternal.length - 1,
                ParentID: this.SelectedItemValue.ID
            });
            LayoutService.RefreshTree.next(true);
        }
    }

    GenerateNewItem() {
        return {};
    }

    DropEntry(event) {
        if (event.previousIndex !== event.currentIndex) {
            if (this.DataSourceInternal) {
                const item = this.DataSourceInternal[event.previousIndex];
                moveItemInArray(this.DataSourceInternal, event.previousIndex, event.currentIndex);
                this.onStyleChanged();
                if (item instanceof LayoutElement) {
                    LayoutService.OnLayoutElementMoved({
                        ElementID: item.ID,
                        Index: event.currentIndex,
                        OldParentID: this.SelectedItemValue.ID,
                        NewParentID: this.SelectedItemValue.ID
                    });
                    LayoutService.RefreshTree.next(true);
                }
            }
        }
    }

    RemoveItem(entry) {
        const list = this.DataSourceInternal;
        if (entry && list) {
            const index = list.indexOf(entry);
            if (index > -1) {
                if (entry instanceof LayoutElement) {
                    let templatePath = '';
                    let Layout = LayoutService.SelectedLayout.getValue();
                    if (Layout && Layout.Workflows) {
                        const parents = MetaHelper.FindParentPath(Layout, entry);
                        if (parents) {
                            parents.forEach(x => {
                                if (x.ElementType === 'template') {
                                    templatePath += x.Name + '.';
                                }
                            });
                        }
                    }
                    const selected = this.SelectedItemValue;
                    LayoutHelper.OnDeleteElement(entry, templatePath, Layout, () => {
                        LayoutService.SelectedItem.next(selected);
                    });   
                } else {
                    const itemID = entry.ID;
                    list.splice(index, 1);
                    const length = list.length;
                    if (length > 0) {
                        if (index < length) {
                            this.SelectedEntry = list[index];
                        } else {
                            this.SelectedEntry = list[length - 1];
                        }
                    } else {
                        this.SelectedEntry = null;
                    }
                    this.onStyleChanged();
                    LayoutService.OnLayoutElementRemoved({
                        ElementID: itemID,
                        ElementName: itemID,
                        ParentID: this.SelectedItemValue.ID
                    });
                }
            }
        }
    }

    onButtonStyleChanged(prop) {
        if (this.SelectedItemValue) {
            if (this.SelectedItemValue.ValuesChanged) {
                this.SelectedItemValue.ValuesChanged.next(prop);
            }
            this.StyleChanged.emit();
            this.cdRef.detectChanges();
        }
    }
    CheckVisibility() {
        if (this.SelectedEntry && this.RawProperties) {
            this.PropertiesValue = this.RawProperties.filter(v => !v.CheckVisibility || v.CheckVisibility(this.SelectedItemValue, this.SelectedEntry));            
        } else {
            this.PropertiesValue = [];
        }
        if (this.ViewInit) {
            this.cdRef.detectChanges();
        }
    }
    GetCaption(data, i) {
        if (this.UseCountAsCaptionValue) {
            return i+1;
        } else {
            return data[this.DisplayMemberPath];
        }
    }
    SetDataSource(item) {
        if (this.SelectedEntry && item && item.instance) {
            item.instance.SelectedItem = this.SelectedEntry;
            if (this.SelectedItemValue[item.ID] == null && item.Type != null) {
                this.SelectedItemValue[item.ID] = new item.Type();
            }
            if (item.Type == null || typeof (item.Type) == 'string') {
                item.instance.PathBinding = item.ID;
            } else {
                item.instance.DataSourceInternal = this.SelectedEntry[item.ID];
            }
        }
        if (item.InitArgs) {
            item.instance.SetProperties(item.InitArgs);
        }
        if (item.Properties) {
            item.instance.Properties = item.Properties;
        }
        if (!item.instance.ExternalSubscriptions) {
            item.instance.ExternalSubscriptions = {};
        }
    }
    ComponentAttached(event, item) {
        if (event && item) {
            item.instance = event.instance;
        }
        if (this.SelectedEntry && item && item.instance) {
            this.SetDataSource(item);
            if (item.SelectedChangeSub) {
                item.SelectedChangeSub.unsubscribe();
                item.SelectedChangeSub = null;
            }
            item.SelectedChangeSub = this.SelectedEntryChange.subscribe(() => {
                this.SetDataSource(item);
            });
            if (item.instance.StyleChanged) {
                if (item.instance.ExternalSubscriptions["StyleChanged"]) {
                    item.instance.ExternalSubscriptions["StyleChanged"].unsubscribe();
                }
                item.instance.ExternalSubscriptions["StyleChanged"] = item.instance.StyleChanged.subscribe((val) => {
                    this.CheckVisibility();
                    this.onStyleChanged();
                });
            }
            this.cdRef.detectChanges();
        }
    }
}